import type {
  Clue,
  ClueStatus,
  ConstructorGrid,
  CrosswordData,
  GridDraft,
  GridFragment,
  GridPlacement,
  ListSide,
  Pairing,
  PairingStatus,
  ScratchpadEntry,
  Word,
} from '../types'
import { createGrid } from './constructor'
import { getLetterArray, getLetters, splitCrossListGroups } from './pairings'

const validPairingStatuses: PairingStatus[] = ['none', 'draft', 'approved', 'rejected']
const validClueStatuses: ClueStatus[] = ['final', 'draft']
const validLists: ListSide[] = ['A', 'B']

export type DataSummary = {
  words: number
  pairings: number
  drafts: number
  placements: number
  clues: number
  finalClues: number
  draftClues: number
  scratchpadEntries: number
  scratchpadPlacements: number
}

export type DataDifference = {
  label: string
  local: number
  remote: number
}

export type DataChangeDetail = {
  category: string
  label: string
  local: string
  remote: string
  kind: 'content' | 'metadata'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function asInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

function asList(value: unknown): ListSide | undefined {
  return validLists.includes(value as ListSide) ? value as ListSide : undefined
}

function asPairingStatus(value: unknown): PairingStatus {
  return validPairingStatuses.includes(value as PairingStatus) ? value as PairingStatus : 'none'
}

function asClueStatus(value: unknown): ClueStatus {
  return validClueStatuses.includes(value as ClueStatus) ? value as ClueStatus : 'draft'
}

function sanitizeClue(value: unknown): Clue | undefined {
  if (!isRecord(value)) return undefined
  const text = asString(value['text'])
  if (text == null || text.trim() === '') return undefined
  const clue: Clue = {
    text,
    status: asClueStatus(value['status']),
  }
  const id = asOptionalString(value['id'])
  const createdAt = asOptionalString(value['createdAt'])
  const updatedAt = asOptionalString(value['updatedAt'])
  if (id) clue.id = id
  if (createdAt) clue.createdAt = createdAt
  if (updatedAt) clue.updatedAt = updatedAt
  return clue
}

function clueKey(clue: Clue): string {
  return clue.text.trim().toLowerCase()
}

function normalizeWordText(text: string): string {
  return text.replace(/\s/g, '').toUpperCase()
}

function wordDisplay(text: string): string {
  return text.trim().toUpperCase()
}

function joinPreview(values: string[], emptyText: string): string {
  if (values.length === 0) return emptyText
  const preview = values.slice(0, 4).join(', ')
  const remaining = values.length - 4
  return remaining > 0 ? `${preview}, +${remaining} more` : preview
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b)
}

function sanitizeDraftClues(value: unknown, currentClue: Clue | undefined): Clue[] {
  const drafts: Clue[] = []
  const seen = new Set<string>()

  if (Array.isArray(value)) {
    for (const item of value) {
      const clue = sanitizeClue(item)
      if (!clue) continue
      const key = clueKey(clue)
      if (seen.has(key)) continue
      seen.add(key)
      drafts.push({
        ...clue,
        id: clue.id ?? `draft-${drafts.length}-${key}`,
        status: 'draft',
      })
    }
  }

  if (currentClue?.status === 'draft') {
    const key = clueKey(currentClue)
    if (!seen.has(key)) {
      drafts.push({
        ...currentClue,
        id: currentClue.id ?? `draft-${drafts.length}-${key}`,
        status: 'draft',
      })
    }
  }

  return drafts
}

function sanitizeWords(value: unknown): Word[] {
  if (!Array.isArray(value)) return []

  const words: Word[] = []
  const seenIds = new Set<string>()
  const seenLettersByList = new Set<string>()

  for (const item of value) {
    if (!isRecord(item)) continue
    const id = asString(item['id'])
    const text = asString(item['text'])
    if (id == null || id === '' || text == null || text.trim() === '') continue
    if (seenIds.has(id)) continue

    const list = asList(item['list'])
    const letters = text.replace(/\s/g, '').toUpperCase()
    if (letters === '') continue

    const duplicateKey = `${list ?? 'none'}:${letters}`
    if (seenLettersByList.has(duplicateKey)) continue

    seenIds.add(id)
    seenLettersByList.add(duplicateKey)
    words.push({
      id,
      text: text.toUpperCase(),
      list,
      groupId: asOptionalString(item['groupId']),
      twinGroupId: asOptionalString(item['twinGroupId']),
    })
  }

  return splitCrossListGroups(words)
}

function sanitizePairings(value: unknown, words: Word[]): Pairing[] {
  if (!Array.isArray(value)) return []

  const wordsById = new Map(words.map(word => [word.id, word]))
  const pairings: Pairing[] = []
  const seenIds = new Set<string>()
  const seenPairs = new Set<string>()

  for (const item of value) {
    if (!isRecord(item)) continue
    const id = asString(item['id'])
    const wordAId = asString(item['wordAId'])
    const wordBId = asString(item['wordBId'])
    if (id == null || wordAId == null || wordBId == null) continue
    if (seenIds.has(id)) continue

    const wordA = wordsById.get(wordAId)
    const wordB = wordsById.get(wordBId)
    if (!wordA || !wordB) continue
    if (getLetters(wordA).length !== getLetters(wordB).length) continue
    if (wordA.list && wordB.list && wordA.list === wordB.list) continue

    const pairKey = `${wordAId}:${wordBId}`
    if (seenPairs.has(pairKey)) continue

    seenIds.add(id)
    seenPairs.add(pairKey)
    const clue = sanitizeClue(item['clue'])
    const clueDrafts = sanitizeDraftClues(item['clueDrafts'], clue)
    pairings.push({
      id,
      wordAId,
      wordBId,
      status: asPairingStatus(item['status']),
      clue,
      clueDrafts: clueDrafts.length > 0 ? clueDrafts : undefined,
    })
  }

  return pairings
}

function sanitizePlacement(value: unknown, validPairingIds: Set<string>): GridPlacement | null {
  if (!isRecord(value)) return null

  const id = asString(value['id'])
  const wordAText = asString(value['wordAText'])
  const wordBText = asString(value['wordBText'])
  const row = asInteger(value['row'])
  const col = asInteger(value['col'])
  const direction = value['direction']
  if (id == null || wordAText == null || wordBText == null || row == null || col == null) return null
  if (direction !== 'across' && direction !== 'down') return null

  const wordALetters = getLetterArray(wordAText)
  const wordBLetters = getLetterArray(wordBText)
  if (wordALetters.length === 0 || wordALetters.length !== wordBLetters.length) return null

  const rawPairingId = value['pairingId']
  const pairingId = typeof rawPairingId === 'string' && validPairingIds.has(rawPairingId)
    ? rawPairingId
    : null

  const priority = asInteger(value['priority'])

  return {
    id,
    pairingId,
    wordAText: wordAText.toUpperCase(),
    wordBText: wordBText.toUpperCase(),
    row,
    col,
    direction,
    isTempA: typeof value['isTempA'] === 'boolean' ? value['isTempA'] : undefined,
    isTempB: typeof value['isTempB'] === 'boolean' ? value['isTempB'] : undefined,
    priority: priority != null && priority > 0 ? priority : undefined,
  }
}

function rebuildGrid(width: number, height: number, rawCells: unknown, placements: GridPlacement[]): ConstructorGrid {
  const grid = createGrid(width, height)

  if (Array.isArray(rawCells)) {
    for (let row = 0; row < height; row++) {
      const rowCells = rawCells[row]
      if (!Array.isArray(rowCells)) continue
      for (let col = 0; col < width; col++) {
        const cell = rowCells[col]
        if (!isRecord(cell)) continue
        if (cell['isBlocked'] === true) {
          const gridCell = grid.cells[row]?.[col]
          if (gridCell) gridCell.isBlocked = true
        }
      }
    }
  }

  for (const placement of placements) {
    grid.placements.push(placement)

    const wordALetters = getLetterArray(placement.wordAText)
    const wordBLetters = getLetterArray(placement.wordBText)
    for (let i = 0; i < wordALetters.length; i++) {
      const row = placement.direction === 'down' ? placement.row + i : placement.row
      const col = placement.direction === 'across' ? placement.col + i : placement.col
      if (row < 0 || row >= height || col < 0 || col >= width) continue

      const cell = grid.cells[row]?.[col]
      const letterA = wordALetters[i]
      const letterB = wordBLetters[i]
      if (!cell || letterA == null || letterB == null) continue
      cell.letterA = letterA
      cell.letterB = letterB
      cell.placementId = placement.id
    }
  }

  return grid
}

function sanitizeGrid(value: unknown, validPairingIds: Set<string>): ConstructorGrid | null {
  if (!isRecord(value)) return null
  const width = asInteger(value['width'])
  const height = asInteger(value['height'])
  if (width == null || height == null) return null
  if (width < 1 || width > 50 || height < 1 || height > 50) return null

  const placements: GridPlacement[] = []
  const seenPlacementIds = new Set<string>()
  const rawPlacements = value['placements']
  if (Array.isArray(rawPlacements)) {
    for (const rawPlacement of rawPlacements) {
      const placement = sanitizePlacement(rawPlacement, validPairingIds)
      if (!placement || seenPlacementIds.has(placement.id)) continue
      seenPlacementIds.add(placement.id)
      placements.push(placement)
    }
  }

  return rebuildGrid(width, height, value['cells'], placements)
}

function sanitizeDrafts(value: unknown, validPairingIds: Set<string>): GridDraft[] {
  if (!Array.isArray(value)) return []

  const drafts: GridDraft[] = []
  const seenIds = new Set<string>()

  for (const item of value) {
    if (!isRecord(item)) continue
    const id = asString(item['id'])
    const name = asString(item['name'])
    const updatedAt = asString(item['updatedAt'])
    const grid = sanitizeGrid(item['grid'], validPairingIds)
    if (id == null || name == null || updatedAt == null || grid == null) continue
    if (seenIds.has(id)) continue

    seenIds.add(id)
    drafts.push({ id, name, updatedAt, grid })
  }

  return drafts
}

function sanitizeFragments(value: unknown, validPairingIds: Set<string>): GridFragment[] {
  if (!Array.isArray(value)) return []

  const fragments: GridFragment[] = []
  const seenIds = new Set<string>()

  for (const item of value) {
    if (!isRecord(item)) continue
    const id = asString(item['id'])
    const name = asString(item['name'])
    const width = asInteger(item['width'])
    const height = asInteger(item['height'])
    const createdAt = asString(item['createdAt'])
    const updatedAt = asString(item['updatedAt'])
    if (id == null || name == null || width == null || height == null) continue
    if (createdAt == null || updatedAt == null) continue
    if (seenIds.has(id)) continue

    const rawPlacements = item['placements']
    if (!Array.isArray(rawPlacements)) continue

    const placements: GridPlacement[] = []
    for (const rawPlacement of rawPlacements) {
      const placement = sanitizePlacement(rawPlacement, validPairingIds)
      if (placement) placements.push(placement)
    }
    if (placements.length === 0) continue

    seenIds.add(id)
    fragments.push({ id, name, width, height, createdAt, updatedAt, placements })
  }

  return fragments
}

function sanitizeScratchpadEntries(value: unknown): ScratchpadEntry[] {
  if (!Array.isArray(value)) return []

  const entries: ScratchpadEntry[] = []
  const seenIds = new Set<string>()

  for (const item of value) {
    if (!isRecord(item)) continue
    const id = asString(item['id'])
    if (id == null || seenIds.has(id)) continue
    seenIds.add(id)
    entries.push({
      id,
      wordA: asString(item['wordA']) ?? '',
      wordB: asString(item['wordB']) ?? '',
      clue: asString(item['clue']) ?? '',
    })
  }

  return entries
}

export function sanitizeCrosswordData(raw: unknown): CrosswordData | null {
  if (!isRecord(raw)) return null
  const hasCrosswordShape =
    Array.isArray(raw['words'])
    || Array.isArray(raw['pairings'])
    || Array.isArray(raw['drafts'])
    || Array.isArray(raw['scratchpadEntries'])
    || isRecord(raw['scratchpadGrid'])
    || isRecord(raw['listNames'])

  if (!hasCrosswordShape) return null

  const words = sanitizeWords(raw['words'])
  const pairings = sanitizePairings(raw['pairings'], words)
  const validPairingIds = new Set(pairings.map(pairing => pairing.id))
  const drafts = sanitizeDrafts(raw['drafts'], validPairingIds)
  const activeDraftIdRaw = raw['activeDraftId']
  const activeDraftId = typeof activeDraftIdRaw === 'string' && drafts.some(draft => draft.id === activeDraftIdRaw)
    ? activeDraftIdRaw
    : null

  const rawListNames = raw['listNames']
  const listNames = isRecord(rawListNames)
    ? {
      A: asString(rawListNames['A']) ?? 'List A',
      B: asString(rawListNames['B']) ?? 'List B',
    }
    : { A: 'List A', B: 'List B' }

  return {
    words,
    pairings,
    listNames,
    drafts,
    activeDraftId,
    scratchpadEntries: sanitizeScratchpadEntries(raw['scratchpadEntries']),
    scratchpadGrid: sanitizeGrid(raw['scratchpadGrid'], validPairingIds),
    fragments: sanitizeFragments(raw['fragments'], validPairingIds),
  }
}

function gridHasBlockedCells(grid: ConstructorGrid): boolean {
  for (const row of grid.cells) {
    for (const cell of row) {
      if (cell.isBlocked) return true
    }
  }
  return false
}

function draftHasMeaningfulData(draft: GridDraft): boolean {
  if (draft.grid.placements.length > 0) return true
  if (gridHasBlockedCells(draft.grid)) return true
  if (draft.grid.width !== 15 || draft.grid.height !== 15) return true
  return draft.name !== 'Untitled'
}

export function hasMeaningfulCrosswordData(data: CrosswordData): boolean {
  if (data.words.length > 0 || data.pairings.length > 0) return true
  if (data.scratchpadEntries?.some(entry => entry.wordA.trim() || entry.wordB.trim() || entry.clue.trim())) return true
  if ((data.scratchpadGrid?.placements.length ?? 0) > 0) return true
  return data.drafts?.some(draftHasMeaningfulData) ?? false
}

export function summarizeCrosswordData(data: CrosswordData): DataSummary {
  let placements = 0
  for (const draft of data.drafts ?? []) {
    placements += draft.grid.placements.length
  }

  const clues = data.pairings.reduce((count, pairing) => {
    const clueTexts = new Set<string>()
    if (pairing.clue?.text.trim()) clueTexts.add(clueKey(pairing.clue))
    for (const draft of pairing.clueDrafts ?? []) {
      if (draft.text.trim()) clueTexts.add(clueKey(draft))
    }
    return count + clueTexts.size
  }, 0)
  const finalClues = data.pairings.filter(pairing => pairing.clue?.text.trim() && pairing.clue.status === 'final').length
  const draftClues = data.pairings.reduce((count, pairing) => {
    if (pairing.clueDrafts && pairing.clueDrafts.length > 0) {
      return count + pairing.clueDrafts.filter(draft => draft.text.trim()).length
    }
    return pairing.clue?.text.trim() && pairing.clue.status === 'draft' ? count + 1 : count
  }, 0)

  return {
    words: data.words.length,
    pairings: data.pairings.length,
    drafts: data.drafts?.length ?? 0,
    placements,
    clues,
    finalClues,
    draftClues,
    scratchpadEntries: data.scratchpadEntries?.length ?? 0,
    scratchpadPlacements: data.scratchpadGrid?.placements.length ?? 0,
  }
}

function getWordKey(word: Word): string {
  return `${word.list ?? 'none'}:${normalizeWordText(word.text)}`
}

function getWordLabel(word: Word): string {
  return `${wordDisplay(word.text)} (${word.list ?? 'unlisted'})`
}

function getWordKeyById(data: CrosswordData): Map<string, string> {
  return new Map(data.words.map(word => [word.id, getWordKey(word)]))
}

function getWordLabelById(data: CrosswordData): Map<string, string> {
  return new Map(data.words.map(word => [word.id, getWordLabel(word)]))
}

function getPairingKey(pairing: Pairing, wordKeyById: Map<string, string>): string {
  return `${wordKeyById.get(pairing.wordAId) ?? pairing.wordAId}|${wordKeyById.get(pairing.wordBId) ?? pairing.wordBId}`
}

function getPairingLabel(pairing: Pairing, wordLabelById: Map<string, string>): string {
  return `${wordLabelById.get(pairing.wordAId) ?? pairing.wordAId} / ${wordLabelById.get(pairing.wordBId) ?? pairing.wordBId}`
}

function normalizeClueForCompare(clue: Clue | undefined): string | null {
  if (!clue?.text.trim()) return null
  return `${clue.status}:${clue.text.trim()}`
}

function normalizeClueDraftsForCompare(clues: Clue[] | undefined): string[] {
  return [...new Set((clues ?? [])
    .filter(clue => clue.text.trim())
    .map(clue => `draft:${clue.text.trim()}`))]
    .sort(compareStrings)
}

function getBlockedCellKeys(grid: ConstructorGrid): string[] {
  const blocked: string[] = []
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      if (grid.cells[row]?.[col]?.isBlocked) blocked.push(`${row},${col}`)
    }
  }
  return blocked.sort(compareStrings)
}

function getPlacementSignature(placement: GridPlacement, wordKeyById: Map<string, string>): string {
  const pairingKey = placement.pairingId ? wordKeyById.get(placement.pairingId) ?? placement.pairingId : 'manual'
  return [
    pairingKey,
    normalizeWordText(placement.wordAText),
    normalizeWordText(placement.wordBText),
    placement.row,
    placement.col,
    placement.direction,
    placement.isTempA === true ? 'tempA' : '',
    placement.isTempB === true ? 'tempB' : '',
    placement.priority ?? '',
  ].join('|')
}

function getPlacementLabel(placement: GridPlacement): string {
  const direction = placement.direction === 'across' ? 'across' : 'down'
  return `${wordDisplay(placement.wordAText)}/${wordDisplay(placement.wordBText)} at ${placement.row + 1},${placement.col + 1} ${direction}`
}

function getGridFingerprint(grid: ConstructorGrid, wordKeyByPairingId: Map<string, string>) {
  return {
    width: grid.width,
    height: grid.height,
    blocked: getBlockedCellKeys(grid),
    placements: grid.placements
      .map(placement => getPlacementSignature(placement, wordKeyByPairingId))
      .sort(compareStrings),
  }
}

function getDraftKey(draft: GridDraft): string {
  return draft.id
}

function getScratchpadEntrySignature(entry: ScratchpadEntry): string {
  return `${wordDisplay(entry.wordA)}|${wordDisplay(entry.wordB)}|${entry.clue.trim()}`
}

function getWordGroupSignature(word: Word, groupMembersByGroupId: Map<string, string[]>): string | null {
  if (!word.groupId) return null
  return groupMembersByGroupId.get(word.groupId)?.join('|') ?? null
}

function buildWordGroupMembers(data: CrosswordData): Map<string, string[]> {
  const groups = new Map<string, string[]>()
  for (const word of data.words) {
    if (!word.groupId) continue
    const members = groups.get(word.groupId) ?? []
    members.push(getWordKey(word))
    groups.set(word.groupId, members)
  }
  for (const [groupId, members] of groups) {
    groups.set(groupId, members.sort(compareStrings))
  }
  return groups
}

function buildPairingWordKeyByPairingId(data: CrosswordData): Map<string, string> {
  const wordKeyById = getWordKeyById(data)
  return new Map(data.pairings.map(pairing => [pairing.id, getPairingKey(pairing, wordKeyById)]))
}

function getComparableCrosswordData(data: CrosswordData) {
  const wordKeyById = getWordKeyById(data)
  const wordGroupMembers = buildWordGroupMembers(data)
  const pairingWordKeyById = buildPairingWordKeyByPairingId(data)
  const activeDraftName = data.activeDraftId
    ? data.drafts?.find(draft => draft.id === data.activeDraftId)?.name ?? null
    : null

  return {
    listNames: data.listNames ?? { A: 'List A', B: 'List B' },
    activeDraftName,
    words: data.words
      .map(word => ({
        key: getWordKey(word),
        text: wordDisplay(word.text),
        list: word.list ?? null,
        group: getWordGroupSignature(word, wordGroupMembers),
      }))
      .sort((a, b) => compareStrings(a.key, b.key)),
    pairings: data.pairings
      .map(pairing => ({
        key: getPairingKey(pairing, wordKeyById),
        status: pairing.status,
        clue: normalizeClueForCompare(pairing.clue),
        clueDrafts: normalizeClueDraftsForCompare(pairing.clueDrafts),
      }))
      .sort((a, b) => compareStrings(a.key, b.key)),
    drafts: (data.drafts ?? [])
      .map(draft => ({
        name: draft.name,
        grid: getGridFingerprint(draft.grid, pairingWordKeyById),
      }))
      .sort((a, b) => compareStrings(`${a.name}:${JSON.stringify(a.grid)}`, `${b.name}:${JSON.stringify(b.grid)}`)),
    scratchpadEntries: (data.scratchpadEntries ?? [])
      .map(getScratchpadEntrySignature)
      .sort(compareStrings),
    scratchpadGrid: data.scratchpadGrid
      ? getGridFingerprint(data.scratchpadGrid, pairingWordKeyById)
      : getGridFingerprint(createGrid(1, 1), pairingWordKeyById),
  }
}

export function getCrosswordDataFingerprint(data: CrosswordData): string {
  return JSON.stringify(getComparableCrosswordData(data))
}

export function areCrosswordDataEquivalent(local: CrosswordData, remote: CrosswordData): boolean {
  return getCrosswordDataFingerprint(local) === getCrosswordDataFingerprint(remote)
}

export function getCrosswordDataDifferences(local: CrosswordData, remote: CrosswordData): DataDifference[] {
  const localSummary = summarizeCrosswordData(local)
  const remoteSummary = summarizeCrosswordData(remote)
  const rows: Array<{ key: keyof DataSummary; label: string }> = [
    { key: 'words', label: 'Words' },
    { key: 'pairings', label: 'Pairings' },
    { key: 'drafts', label: 'Drafts' },
    { key: 'placements', label: 'Placed entries' },
    { key: 'clues', label: 'Clues' },
    { key: 'finalClues', label: 'Final clues' },
    { key: 'draftClues', label: 'Draft clues' },
    { key: 'scratchpadEntries', label: 'Scratchpad notes' },
    { key: 'scratchpadPlacements', label: 'Scratchpad placements' },
  ]

  return rows
    .map(row => ({
      label: row.label,
      local: localSummary[row.key],
      remote: remoteSummary[row.key],
    }))
    .filter(row => row.local !== row.remote)
}

function pushSetDetails(
  details: DataChangeDetail[],
  category: string,
  label: string,
  localOnly: string[],
  remoteOnly: string[],
  kind: DataChangeDetail['kind'] = 'content'
) {
  if (localOnly.length === 0 && remoteOnly.length === 0) return
  details.push({
    category,
    label,
    local: joinPreview(localOnly.sort(compareStrings), 'None'),
    remote: joinPreview(remoteOnly.sort(compareStrings), 'None'),
    kind,
  })
}

function diffSets(localValues: Iterable<string>, remoteValues: Iterable<string>) {
  const localSet = new Set(localValues)
  const remoteSet = new Set(remoteValues)
  return {
    localOnly: [...localSet].filter(value => !remoteSet.has(value)),
    remoteOnly: [...remoteSet].filter(value => !localSet.has(value)),
  }
}

function describeGridDifference(
  localGrid: ConstructorGrid,
  remoteGrid: ConstructorGrid,
  pairingKeyById: Map<string, string>
): { local: string; remote: string } | null {
  const differences: string[] = []
  if (localGrid.width !== remoteGrid.width || localGrid.height !== remoteGrid.height) {
    differences.push('size')
  }
  const blockedDiff = diffSets(getBlockedCellKeys(localGrid), getBlockedCellKeys(remoteGrid))
  if (blockedDiff.localOnly.length > 0 || blockedDiff.remoteOnly.length > 0) {
    differences.push('black cells')
  }
  const localPlacementBySignature = new Map(localGrid.placements.map(placement => [
    getPlacementSignature(placement, pairingKeyById),
    getPlacementLabel(placement),
  ]))
  const remotePlacementBySignature = new Map(remoteGrid.placements.map(placement => [
    getPlacementSignature(placement, pairingKeyById),
    getPlacementLabel(placement),
  ]))
  const placementDiff = diffSets(localPlacementBySignature.keys(), remotePlacementBySignature.keys())
  if (placementDiff.localOnly.length > 0 || placementDiff.remoteOnly.length > 0) {
    return {
      local: joinPreview(placementDiff.localOnly.map(key => localPlacementBySignature.get(key) ?? key), differences.length > 0 ? differences.join(', ') : 'No placement changes'),
      remote: joinPreview(placementDiff.remoteOnly.map(key => remotePlacementBySignature.get(key) ?? key), differences.length > 0 ? differences.join(', ') : 'No placement changes'),
    }
  }
  if (differences.length === 0) return null
  return {
    local: `${localGrid.width}x${localGrid.height}, ${getBlockedCellKeys(localGrid).length} black cells`,
    remote: `${remoteGrid.width}x${remoteGrid.height}, ${getBlockedCellKeys(remoteGrid).length} black cells`,
  }
}

export function getCrosswordDataChangeDetails(local: CrosswordData, remote: CrosswordData): DataChangeDetail[] {
  const details: DataChangeDetail[] = []
  const localWordKeyById = getWordKeyById(local)
  const remoteWordKeyById = getWordKeyById(remote)
  const localWordLabelById = getWordLabelById(local)
  const remoteWordLabelById = getWordLabelById(remote)
  const localPairingKeyById = buildPairingWordKeyByPairingId(local)
  const remotePairingKeyById = buildPairingWordKeyByPairingId(remote)
  const pairingKeyById = new Map([...localPairingKeyById, ...remotePairingKeyById])

  if ((local.listNames?.A ?? 'List A') !== (remote.listNames?.A ?? 'List A') || (local.listNames?.B ?? 'List B') !== (remote.listNames?.B ?? 'List B')) {
    details.push({
      category: 'Lists',
      label: 'List names differ',
      local: `${local.listNames?.A ?? 'List A'} / ${local.listNames?.B ?? 'List B'}`,
      remote: `${remote.listNames?.A ?? 'List A'} / ${remote.listNames?.B ?? 'List B'}`,
      kind: 'content',
    })
  }

  const wordDiff = diffSets(local.words.map(getWordKey), remote.words.map(getWordKey))
  pushSetDetails(
    details,
    'Words',
    'Word list contents differ',
    wordDiff.localOnly.map(key => {
      const word = local.words.find(item => getWordKey(item) === key)
      return word ? getWordLabel(word) : key
    }),
    wordDiff.remoteOnly.map(key => {
      const word = remote.words.find(item => getWordKey(item) === key)
      return word ? getWordLabel(word) : key
    })
  )

  const localGroupMembers = buildWordGroupMembers(local)
  const remoteGroupMembers = buildWordGroupMembers(remote)
  for (const word of local.words) {
    const remoteWord = remote.words.find(item => getWordKey(item) === getWordKey(word))
    if (!remoteWord) continue
    const localGroup = getWordGroupSignature(word, localGroupMembers)
    const remoteGroup = getWordGroupSignature(remoteWord, remoteGroupMembers)
    if (localGroup !== remoteGroup) {
      details.push({
        category: 'Words',
        label: `${wordDisplay(word.text)} variant group differs`,
        local: localGroup ?? 'No group',
        remote: remoteGroup ?? 'No group',
        kind: 'content',
      })
    }
  }

  const localPairingByKey = new Map(local.pairings.map(pairing => [getPairingKey(pairing, localWordKeyById), pairing]))
  const remotePairingByKey = new Map(remote.pairings.map(pairing => [getPairingKey(pairing, remoteWordKeyById), pairing]))
  const pairingDiff = diffSets(localPairingByKey.keys(), remotePairingByKey.keys())
  pushSetDetails(
    details,
    'Pairs',
    'Pair list contents differ',
    pairingDiff.localOnly.map(key => {
      const pairing = localPairingByKey.get(key)
      return pairing ? getPairingLabel(pairing, localWordLabelById) : key
    }),
    pairingDiff.remoteOnly.map(key => {
      const pairing = remotePairingByKey.get(key)
      return pairing ? getPairingLabel(pairing, remoteWordLabelById) : key
    })
  )

  for (const [key, localPairing] of localPairingByKey) {
    const remotePairing = remotePairingByKey.get(key)
    if (!remotePairing) continue
    const label = getPairingLabel(localPairing, localWordLabelById)
    if (localPairing.status !== remotePairing.status) {
      details.push({
        category: 'Pairs',
        label: `${label} status differs`,
        local: localPairing.status,
        remote: remotePairing.status,
        kind: 'content',
      })
    }

    const localClue = normalizeClueForCompare(localPairing.clue)
    const remoteClue = normalizeClueForCompare(remotePairing.clue)
    if (localClue !== remoteClue) {
      details.push({
        category: 'Clues',
        label: `${label} current clue differs`,
        local: localPairing.clue?.text.trim() || 'No current clue',
        remote: remotePairing.clue?.text.trim() || 'No current clue',
        kind: 'content',
      })
    }

    const clueDraftDiff = diffSets(
      normalizeClueDraftsForCompare(localPairing.clueDrafts),
      normalizeClueDraftsForCompare(remotePairing.clueDrafts)
    )
    pushSetDetails(
      details,
      'Clues',
      `${label} draft clues differ`,
      clueDraftDiff.localOnly.map(value => value.replace(/^draft:/, '')),
      clueDraftDiff.remoteOnly.map(value => value.replace(/^draft:/, ''))
    )

    if (localClue === remoteClue && JSON.stringify(normalizeClueDraftsForCompare(localPairing.clueDrafts)) === JSON.stringify(normalizeClueDraftsForCompare(remotePairing.clueDrafts))) {
      const localMeta = JSON.stringify({
        current: localPairing.clue ? { id: localPairing.clue.id, createdAt: localPairing.clue.createdAt, updatedAt: localPairing.clue.updatedAt } : null,
        drafts: (localPairing.clueDrafts ?? []).map(clue => ({ id: clue.id, createdAt: clue.createdAt, updatedAt: clue.updatedAt })),
      })
      const remoteMeta = JSON.stringify({
        current: remotePairing.clue ? { id: remotePairing.clue.id, createdAt: remotePairing.clue.createdAt, updatedAt: remotePairing.clue.updatedAt } : null,
        drafts: (remotePairing.clueDrafts ?? []).map(clue => ({ id: clue.id, createdAt: clue.createdAt, updatedAt: clue.updatedAt })),
      })
      if (localMeta !== remoteMeta) {
        details.push({
          category: 'Metadata',
          label: `${label} clue metadata differs`,
          local: 'IDs or timestamps differ',
          remote: 'IDs or timestamps differ',
          kind: 'metadata',
        })
      }
    }
  }

  const localDraftById = new Map((local.drafts ?? []).map(draft => [getDraftKey(draft), draft]))
  const remoteDraftById = new Map((remote.drafts ?? []).map(draft => [getDraftKey(draft), draft]))
  const draftDiff = diffSets(localDraftById.keys(), remoteDraftById.keys())
  pushSetDetails(
    details,
    'Drafts',
    'Draft list differs',
    draftDiff.localOnly.map(key => localDraftById.get(key)?.name ?? key),
    draftDiff.remoteOnly.map(key => remoteDraftById.get(key)?.name ?? key)
  )

  const localActiveDraftName = local.activeDraftId ? localDraftById.get(local.activeDraftId)?.name ?? local.activeDraftId : 'None'
  const remoteActiveDraftName = remote.activeDraftId ? remoteDraftById.get(remote.activeDraftId)?.name ?? remote.activeDraftId : 'None'
  if (localActiveDraftName !== remoteActiveDraftName) {
    details.push({
      category: 'Drafts',
      label: 'Active draft differs',
      local: localActiveDraftName,
      remote: remoteActiveDraftName,
      kind: 'content',
    })
  }

  const sharedDraftIds = [...localDraftById.keys()].filter(key => remoteDraftById.has(key))
  for (const draftId of sharedDraftIds) {
    const localDraft = localDraftById.get(draftId)!
    const remoteDraft = remoteDraftById.get(draftId)!
    if (localDraft.name !== remoteDraft.name) {
      details.push({
        category: 'Drafts',
        label: `Draft name differs`,
        local: localDraft.name,
        remote: remoteDraft.name,
        kind: 'content',
      })
    }

    const gridDiff = describeGridDifference(localDraft.grid, remoteDraft.grid, pairingKeyById)
    if (gridDiff) {
      details.push({
        category: 'Drafts',
        label: `${localDraft.name} grid differs`,
        ...gridDiff,
        kind: 'content',
      })
    } else if (localDraft.updatedAt !== remoteDraft.updatedAt) {
      details.push({
        category: 'Metadata',
        label: `${localDraft.name} updated time differs`,
        local: localDraft.updatedAt,
        remote: remoteDraft.updatedAt,
        kind: 'metadata',
      })
    }
  }

  const scratchpadEntryDiff = diffSets(
    (local.scratchpadEntries ?? []).map(getScratchpadEntrySignature),
    (remote.scratchpadEntries ?? []).map(getScratchpadEntrySignature)
  )
  pushSetDetails(details, 'Scratchpad', 'Scratchpad notes differ', scratchpadEntryDiff.localOnly, scratchpadEntryDiff.remoteOnly)

  const localScratchpadGrid = local.scratchpadGrid ?? createGrid(1, 1)
  const remoteScratchpadGrid = remote.scratchpadGrid ?? createGrid(1, 1)
  const scratchpadGridDiff = describeGridDifference(localScratchpadGrid, remoteScratchpadGrid, pairingKeyById)
  if (scratchpadGridDiff) {
    details.push({
      category: 'Scratchpad',
      label: 'Scratchpad canvas differs',
      ...scratchpadGridDiff,
      kind: 'content',
    })
  }

  return details
}

export function inferCrosswordDataUpdatedAt(data: CrosswordData): string | null {
  let newest: string | null = null
  const consider = (value: string | undefined) => {
    if (!value) return
    if (newest == null || value > newest) newest = value
  }

  for (const draft of data.drafts ?? []) {
    consider(draft.updatedAt)
  }
  for (const pairing of data.pairings) {
    consider(pairing.clue?.updatedAt)
    consider(pairing.clue?.createdAt)
    for (const draftClue of pairing.clueDrafts ?? []) {
      consider(draftClue.updatedAt)
      consider(draftClue.createdAt)
    }
  }
  return newest
}
